import React from "react"
import {useQueryState} from "use-location-state"
import {useAccessToken, useIsUnitUser} from "../../services/auth"
import {useParams} from "react-router"
import {useRefreshToken} from "../../hooks/useRefreshToken"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {ErrorDocument, OkDocument} from "../../resources/common"
import {
	getReceivedPayment,
	isWireReceivedPayment,
	RealtimeReceivedPayment,
	ReceivedPaymentTypes,
} from "../../resources/receivedPayments"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import classNames from "classnames"
import {ReceivedPaymentComponent} from "../../components/ReceivedPayments/ReceivedPayment"
import ReactJson from "react-json-view"
import {IncomingAch} from "../../resources/incomingAch"
import {getIncomingWireFromIncluded, IncomingWire} from "../../resources/incomingWire"
import {UnitUserOnly} from "../../containers/PermissionedUser/PermissionedUser"
import Card from "../../containers/Card/Card"
import MainSectionLayout from "../../containers/MainSection/MainSectionLayout"
import ReceivedPaymentTitleBar from "./ReceivedPaymentTitleBar"
import PaymentTabs, {PaymentTabConstants} from "../Payments/PaymentTabs"
import {paymentTabQueryStateName} from "../Payments/Payment"
import {AsyncResultRequestState} from "../../types/asyncResult"
import {ReceivedPayment} from "../../resources/receivedPayments"
import {ReceivedPaymentAssignments, ReceivedPaymentAssignmentsProps} from "./ReceivedPaymentAssignments"
import {Customer, CustomerResourceTypes, isBusinessCustomer} from "../../resources/customer"

export function IncomingAchItemData({rawData}: {rawData: object}) {
	return (
		<UnitUserOnly>
			<div className={"received-payments-incoming-ach-details"}>
				<Card title={"ACH Item"}>
					<ReactJson src={rawData} displayDataTypes={false} />
				</Card>
			</div>
		</UnitUserOnly>
	)
}

interface ReceivedPaymentMainSectionProps {
	incomingAch?: IncomingAch
	incomingWire?: IncomingWire
	isUnitUser: boolean
	receivedPaymentId: string
	receivedPaymentAsyncResultReq: AsyncResultRequestState<OkDocument<ReceivedPayment>, ErrorDocument>
	refresh: () => void
	accessToken: string
	realtimeReceivedPayment?: RealtimeReceivedPayment["attributes"]
}

export function RealtimeReceivedPaymentData({rawData}: {rawData: RealtimeReceivedPayment["attributes"]}) {
	return (
		<UnitUserOnly>
			<div className={"received-payments-realtime-received-payment-details"}>
				<Card title={"Realtime Received Payment"}>
					<ReactJson src={rawData} displayDataTypes={false} />
				</Card>
			</div>
		</UnitUserOnly>
	)
}

function ReceivedPaymentMainSection({
	incomingAch,
	incomingWire,
	isUnitUser,
	receivedPaymentId,
	receivedPaymentAsyncResultReq,
	refresh,
	accessToken,
	realtimeReceivedPayment,
}: ReceivedPaymentMainSectionProps) {
	const hasSidePanel = !!(incomingAch || incomingWire || realtimeReceivedPayment) && isUnitUser
	const renderAfter =
		incomingAch && isUnitUser ? (
			<IncomingAchItemData rawData={incomingAch.attributes.item} />
		) : realtimeReceivedPayment && isUnitUser ? (
			<RealtimeReceivedPaymentData rawData={realtimeReceivedPayment} />
		) : null

	return (
		<MainSectionLayout
			outerWrapperClassname={classNames(hasSidePanel && "received-payments-full-with-details")}
			renderAfter={renderAfter}
			fullWidth={hasSidePanel}
		>
			<ReceivedPaymentComponent
				receivedPaymentId={receivedPaymentId}
				accessToken={accessToken}
				useDrawer={false}
				refresh={refresh}
				receivedPaymentAsyncResult={receivedPaymentAsyncResultReq}
			/>
		</MainSectionLayout>
	)
}

interface ReceivedPaymentPageContentProps {
	isShowAssignments: boolean
	currentTab: PaymentTabConstants
	receivedPaymentAsMainSectionProps: ReceivedPaymentMainSectionProps
	receivedPaymentAssignmentsProps: ReceivedPaymentAssignmentsProps
}

function ReceivedPaymentPageContent({
	isShowAssignments,
	currentTab,
	receivedPaymentAsMainSectionProps,
	receivedPaymentAssignmentsProps,
}: ReceivedPaymentPageContentProps) {
	if (!isShowAssignments) {
		return <ReceivedPaymentMainSection {...receivedPaymentAsMainSectionProps} />
	}
	switch (currentTab) {
		case PaymentTabConstants.FullWireDetails:
			return <ReceivedPaymentMainSection {...receivedPaymentAsMainSectionProps} />
		case PaymentTabConstants.ActionsRequired:
			return <ReceivedPaymentAssignments {...receivedPaymentAssignmentsProps} />
	}
}

export default function ReceivedPaymentPage({assignmentsView = false}: {assignmentsView?: boolean}) {
	const [currentTab, setCurrentTab] = useQueryState<PaymentTabConstants>(
		paymentTabQueryStateName,
		PaymentTabConstants.ActionsRequired
	)
	const accessToken = useAccessToken()
	const {receivedPaymentId = ""} = useParams<{receivedPaymentId: string}>()
	const [refreshToken, refresh] = useRefreshToken()
	const isUnitUser = useIsUnitUser()
	const receivedPaymentAsyncResultReq = useAsyncResult(
		() =>
			getReceivedPayment(
				accessToken,
				receivedPaymentId,
				"customer,account,incomingAch,incomingWire,realtimeReceivedPayment"
			),
		[refreshToken]
	)

	return (
		<div className={"received-payments-full-page"}>
			<AsyncResultComponent asyncResult={receivedPaymentAsyncResultReq}>
				{({value: receivedPaymentResponse}) => {
					const receivedPayment = receivedPaymentResponse.data
					const customers =
						receivedPaymentResponse.included &&
						(receivedPaymentResponse.included.filter((i) => CustomerResourceTypes.includes(i.type)) as Customer[])
					const incomingAch =
						receivedPaymentResponse.included &&
						(receivedPaymentResponse.included.find((i) => i.type === "incomingAch") as IncomingAch)
					const incomingWire = getIncomingWireFromIncluded(receivedPaymentResponse.included)
					const applicationId =
						customers && customers.length > 0 && isBusinessCustomer(customers[0])
							? customers[0].relationships.application?.data.id
							: undefined

					const receivedPaymentAsMainSectionProps: ReceivedPaymentMainSectionProps = {
						incomingAch,
						incomingWire,
						isUnitUser,
						receivedPaymentId,
						receivedPaymentAsyncResultReq,
						refresh,
						accessToken,
						realtimeReceivedPayment:
							receivedPayment.type === ReceivedPaymentTypes.RTP ? receivedPayment.attributes : undefined,
					}
					const receivedPaymentAssignmentsProps: ReceivedPaymentAssignmentsProps = {
						receivedPayment,
						included: receivedPaymentResponse.included || [],
						businessApplicationId: applicationId,
						accessToken,
						refreshToken,
						refresh,
					}
					const hasApprovals = isWireReceivedPayment(receivedPayment) && !!receivedPayment.relationships.approvals
					const isShowAssignments = hasApprovals && assignmentsView

					return (
						<>
							<ReceivedPaymentTitleBar
								receivedPayment={receivedPayment}
								leftSideClassName={classNames(isShowAssignments && "assignments-title-bar-left-side")}
								className={classNames(isShowAssignments && ["assignments-title-bar", "no-padding", "no-align-items"])}
							>
								{isShowAssignments ? (
									<div className="assignment-page-header-right">
										<PaymentTabs
											currentTab={currentTab}
											setCurrentTab={setCurrentTab}
											wirePayment={receivedPayment}
											refreshToken={refreshToken}
										/>
									</div>
								) : null}
							</ReceivedPaymentTitleBar>
							<ReceivedPaymentPageContent
								isShowAssignments={isShowAssignments}
								currentTab={currentTab}
								receivedPaymentAsMainSectionProps={receivedPaymentAsMainSectionProps}
								receivedPaymentAssignmentsProps={receivedPaymentAssignmentsProps}
							/>
						</>
					)
				}}
			</AsyncResultComponent>
		</div>
	)
}
